'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState, useEffect } from 'react';
import Image from 'next/image';
import { siteConfig } from '@/config/site';


const v2PreviewHrefs: Record<string, string> = {
  '/': '#home',
  '/how-it-works': '#how-it-works',
  '/pricing': '#pricing',
  '/about': '#about',
  '/testimonials': '#testimonials',
  '/contact': '#contact',
  '/get-started': '#get-started',
};

function previewHref(href: string, isV2Preview: boolean) {
  return isV2Preview ? (v2PreviewHrefs[href] || href) : href;
}

// The 2SOLV price pill stays in this file. Set this true to show it again.
const show2SolvTicker = false;

const desktopNavHrefs = new Set([
  '/',
  'https://token.2solutions.co',
  'https://exchange.2solutions.co',
  'https://wallet.2solutions.co',
  '/how-it-works',
  '/pricing',
  '/about',
  '/testimonials',
  '/contact',
]);

export default function Header() {
  const pathname = usePathname();
  const isV2Preview = pathname === '/new' || pathname === '/';
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [tokenPrice, setTokenPrice] = useState<string>("...");
  const [tokenChange, setTokenChange] = useState<number | null>(null);
  const [priceFlash, setPriceFlash] = useState(false);
  const desktopNav = siteConfig.mainNav.filter((item) => desktopNavHrefs.has(item.href));
  const navItems = siteConfig.mainNav.map((item) => ({ ...item, href: previewHref(item.href, isV2Preview) }));
  const desktopItems = desktopNav.map((item) => ({ ...item, href: previewHref(item.href, isV2Preview) }));

  useEffect(() => {
    setIsMounted(true);
    
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    
    const refreshPrice = async () => {
      try {
        const endpoint = show2SolvTicker ? "/api/exchange/price" : "/api/wtmtv/spot";
        const response = await fetch(endpoint, { cache: "no-store" });
        if (!response.ok) throw new Error("price unavailable");
        const data = await response.json();
        const price = Number(data.price_usd || 0);
        const change = data.change_24h == null ? null : Number(data.change_24h);
        const rounded = price >= 1 ? price.toFixed(2) : price.toFixed(4);
        setTokenPrice("$" + rounded);
        setTokenChange(Number.isFinite(change) ? change : null);
        setPriceFlash(true);
        setTimeout(() => setPriceFlash(false), 900);
      } catch {
        setTokenPrice("...");
      }
    };

    refreshPrice();
    const priceTimer = window.setInterval(refreshPrice, 12000);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.clearInterval(priceTimer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen((open) => !open);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const isTokenUp = (tokenChange ?? 0) >= 0;

  // Prevent hydration mismatch
  if (!isMounted) {
    return (
      <header className="fixed w-full z-50 bg-black/90 backdrop-blur-md py-2">
        <div className="container mx-auto px-4 h-16 flex items-center">
          <div className="w-48 h-10 bg-gray-700 rounded animate-pulse"></div>
        </div>
      </header>
    );
  }

  return (
    <header 
      className={`fixed top-0 left-0 w-full z-[100] transition-all duration-300 ${
        scrolled ? 'bg-black/90 backdrop-blur-md py-2' : 'bg-black/50 backdrop-blur-sm py-3'
      }`}
    >
      <div className="container mx-auto px-4 flex items-center justify-between gap-4">
        <div className="logo shrink-0">
          <Link href={isV2Preview ? "#home" : "/"} className="flex items-center">
            <Image 
              src="/images/logo.png" 
              alt={`${siteConfig.name} Logo`} 
              width={180} 
              height={50}
              className="h-7 w-auto sm:h-8 md:h-9"
              priority
            />
          </Link>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex flex-1 justify-center">
          <ul className="flex items-center gap-5 xl:gap-7">
            {desktopItems.map((item) => (
              <li key={item.href}>
                <Link 
                  href={item.href}
                  className="text-white hover:text-blue-400 transition-colors font-semibold text-xs xl:text-sm uppercase tracking-wider whitespace-nowrap"
                >
                  {item.title}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0 min-w-0">
          <a
            href="tel:+18773527658"
            className="inline-flex items-center rounded-full border border-amber-300/55 bg-slate-950/90 px-2 py-1 text-[0.56rem] sm:text-[0.66rem] font-black leading-none tracking-[0.08em] text-amber-100 no-underline shadow-[0_0_14px_rgba(250,204,21,0.16)] backdrop-blur transition-all hover:border-amber-300/90 hover:shadow-[0_0_14px_rgba(250,204,21,0.28)] whitespace-nowrap"
            title="Call 877-352-2SOLV — press 1 for a live agent"
          >
            877 352 2SOLV
          </a>
          {show2SolvTicker ? (
          <a
            href="https://token.2solutions.co"
            className={`inline-flex items-center gap-1 rounded-full border border-cyan-300/35 bg-slate-950/90 px-2 py-1 text-[0.56rem] sm:text-[0.66rem] font-bold leading-none text-slate-200 no-underline shadow-[0_0_18px_rgba(15,23,42,0.35)] backdrop-blur transition-all hover:border-cyan-300/70 hover:shadow-[0_0_12px_rgba(73,230,255,0.25)] whitespace-nowrap ${priceFlash ? 'bg-cyan-300/20 border-cyan-300/90' : ''}`}
            title="Open the 2SOLV token portal"
          >
            <span className="tracking-[0.05em] text-cyan-300">2SOLV</span>
            <span className="font-bold tabular-nums text-emerald-50">{tokenPrice}</span>
            {tokenChange !== null && (
              <span className={`text-[0.56rem] sm:text-[0.6rem] ${isTokenUp ? 'text-emerald-400' : 'text-rose-400'}`}>
                {isTokenUp ? '▲' : '▼'}{Math.abs(tokenChange).toFixed(2)}%
              </span>
            )}
          </a>
          ) : (
          <a
            href="https://token.toddmichael.tv"
            className={`inline-flex items-center gap-1 rounded-full border border-cyan-300/35 bg-slate-950/90 px-2 py-1 text-[0.56rem] sm:text-[0.66rem] font-bold leading-none text-slate-200 no-underline shadow-[0_0_18px_rgba(15,23,42,0.35)] backdrop-blur transition-all hover:border-cyan-300/70 hover:shadow-[0_0_12px_rgba(73,230,255,0.25)] whitespace-nowrap ${priceFlash ? 'bg-cyan-300/20 border-cyan-300/90' : ''}`}
            title="Open the wTMTV token page"
          >
            <span className="tracking-[0.05em] text-cyan-300">wTMTV</span>
            <span className="font-bold tabular-nums text-emerald-50">{tokenPrice}</span>
            {tokenChange !== null && (
              <span className={`text-[0.56rem] sm:text-[0.6rem] ${isTokenUp ? 'text-emerald-400' : 'text-rose-400'}`}>
                {isTokenUp ? '▲' : '▼'}{Math.abs(tokenChange).toFixed(2)}%
              </span>
            )}
          </a>
          )}
          <Link 
            href={isV2Preview ? "#get-started" : "/get-started"} 
            className="hidden xl:inline-flex bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-full transition-colors text-sm font-semibold whitespace-nowrap"
          >
            Get Started
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button 
          type="button"
          className="lg:hidden relative z-[120] inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-slate-950/80 text-white text-2xl shadow-lg active:scale-95"
          onClick={toggleMenu}
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          aria-controls="mobile-navigation"
          aria-expanded={isMenuOpen}
        >
          {isMenuOpen ? '✕' : '☰'}
        </button>

        {/* Mobile Navigation */}
        <div
          id="mobile-navigation"
          className={`lg:hidden fixed left-3 right-3 top-[4.6rem] z-[110] origin-top rounded-3xl border border-white/10 bg-slate-950/98 p-4 shadow-2xl shadow-black/45 backdrop-blur-xl transition-all duration-200 ${
            isMenuOpen ? 'translate-y-0 scale-100 opacity-100 pointer-events-auto' : '-translate-y-2 scale-95 opacity-0 pointer-events-none'
          }`}
        >
          <div className="mb-3 flex items-center justify-between border-b border-white/10 pb-3">
            <Link href={isV2Preview ? "#home" : "/"} className="inline-flex items-center" onClick={closeMenu}>
              <Image 
                src="/images/logo.png" 
                alt={`${siteConfig.name} Logo`}
                width={135}
                height={38}
                className="h-8 w-auto"
              />
            </Link>
            <span className="rounded-full border border-cyan-300/30 bg-cyan-300/10 px-3 py-1 text-[0.65rem] font-black uppercase tracking-[0.18em] text-cyan-100">Menu</span>
          </div>
          
          <nav aria-label="Mobile navigation">
            <ul className="grid gap-1">
              {navItems.map((item) => (
                <li key={item.href}>
                  <Link 
                    href={item.href}
                    className="block rounded-2xl px-4 py-3 text-base font-bold text-white transition-colors hover:bg-white/10 hover:text-cyan-200 active:bg-white/15"
                    onClick={closeMenu}
                  >
                    {item.title}
                  </Link>
                </li>
              ))}
              <li className="mt-3 border-t border-white/10 pt-4">
                <Link 
                  href={isV2Preview ? "#get-started" : "/get-started"} 
                  className="block rounded-full bg-blue-600 px-6 py-3 text-center font-black text-white transition-colors hover:bg-blue-700"
                  onClick={closeMenu}
                >
                  Get Started
                </Link>
              </li>
            </ul>
          </nav>
        </div>
        {isMenuOpen && (
          <button
            type="button"
            aria-label="Close mobile menu backdrop"
            className="lg:hidden fixed inset-0 z-[90] cursor-default bg-black/25"
            onClick={closeMenu}
          />
        )}
      </div>
    </header>
  );
}
